import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import type { CountryRow, CurrencyRegion } from '@/lib/regions';

export const QK_REGIONS = ['currency-regions'];

export function useCurrencyRegions() {
  return useQuery({
    queryKey: QK_REGIONS,
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/global-admin/currency-regions/');
      return res.data as { regions: CurrencyRegion[]; countries: CountryRow[] };
    },
  });
}

export function useRegionMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: QK_REGIONS });

  const add = useMutation({
    mutationFn: async (body: { name: string; slug?: string; currency: string; notes?: string }) => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.post('/global-admin/currency-regions/', body)).data as CurrencyRegion;
    },
    onSuccess: refresh,
  });

  const patch = useMutation({
    mutationFn: async ({ id, ...body }: { id: string; name?: string; currency?: string; notes?: string }) => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.patch(`/global-admin/currency-regions/${id}/`, body)).data as CurrencyRegion;
    },
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.delete(`/global-admin/currency-regions/${id}/`)).data as {
        deleted: string;
        reassigned_countries: string[];
        to: string | null;
      };
    },
    onSuccess: refresh,
  });

  const assign = useMutation({
    mutationFn: async ({ id, countries }: { id: string; countries: string[] }) => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.post(`/global-admin/currency-regions/${id}/countries/`, { countries })).data as {
        moved: { code: string; from: string; to: string }[];
        added: string[];
      };
    },
    onSuccess: refresh,
  });

  const unassign = useMutation({
    mutationFn: async ({ id, code }: { id: string; code: string }) => {
      const api = await authenticatedBitswanBackendInstance();
      await api.delete(`/global-admin/currency-regions/${id}/countries/${code}/`);
    },
    onSuccess: refresh,
  });

  const makeDefault = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.post(`/global-admin/currency-regions/${id}/default/`, {})).data as CurrencyRegion;
    },
    onSuccess: refresh,
  });

  return { add, patch, remove, assign, unassign, makeDefault };
}
